import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHeart,
  faMessage,
  faPhone,
  faVideo,
  faCalendar,
  faUserMinus,
  faLock
} from '@fortawesome/free-solid-svg-icons';
import buddyService from '../../services/buddyService';
import { toast } from 'react-toastify';

const INTERACTION_TYPES = [
  { value: 'message', label: 'Message', icon: faMessage, color: '#3b82f6' },
  { value: 'call', label: 'Phone Call', icon: faPhone, color: '#10b981' },
  { value: 'meeting', label: 'Video Meeting', icon: faVideo, color: '#8b5cf6' },
];

const CurrentMatchView = ({ matchData, onRefresh, onBookSession, isPremium }) => {
  const navigate = useNavigate();
  const [showLogInteraction, setShowLogInteraction] = useState(false);
  const [interactionType, setInteractionType] = useState('message');
  const [interactionNotes, setInteractionNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const { match, buddy, recent_interactions, is_facilitator } = matchData;
  
  console.log('🔍 CurrentMatchView - isPremium:', isPremium);
  console.log('🔍 CurrentMatchView - showLogInteraction:', showLogInteraction);

  const handleLogInteractionClick = () => {
    console.log('🔍 handleLogInteractionClick called, isPremium:', isPremium);
    // Check if user is enterprise before allowing to log interaction
    if (!isPremium) {
      console.log('🔍 User is NOT premium, showing upgrade modal');
      onBookSession(); // This will show the premium modal for free users
      return;
    }
    console.log('🔍 User IS premium, toggling form. Current showLogInteraction:', showLogInteraction);
    setShowLogInteraction(!showLogInteraction);
  };

  const handleLogInteraction = async () => {
    if (!interactionNotes.trim()) {
      toast.warning('Please add some notes');
      return;
    }

    setLoading(true);
    try {
      await buddyService.logInteraction({
        match_id: match.id,
        interaction_type: interactionType,
        notes: interactionNotes
      });
      toast.success('Interaction logged!');
      setShowLogInteraction(false);
      setInteractionNotes('');
      onRefresh();
    } catch (error) {
      toast.error('Failed to log interaction');
    } finally {
      setLoading(false);
    }
  };

  const handleEndMatch = async () => {
    if (!window.confirm('Are you sure you want to end this buddy match?')) {
      return;
    }

    try {
      await buddyService.deactivateMatch(match.id);
      toast.success('Match ended');
      onRefresh();
    } catch (error) {
      toast.error('Failed to end match');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInteractionIcon = (type) => {
    const interaction = INTERACTION_TYPES.find(i => i.value === type);
    return interaction ? interaction.icon : faMessage;
  };

  const getInteractionColor = (type) => {
    const interaction = INTERACTION_TYPES.find(i => i.value === type);
    return interaction ? interaction.color : '#999';
  };

  return (
    <div style={{
      backgroundColor: '#1a1a1a',
      border: '2px solid #FFB6C1',
      borderRadius: '12px',
      padding: '2rem',
      marginBottom: '2rem'
    }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h2 style={{ 
            color: '#FFB6C1', 
            marginBottom: '0.5rem',
            fontSize: '1.75rem'
          }}>
            <FontAwesomeIcon icon={faHeart} /> Your Current Match
          </h2>
          <p style={{ color: '#999' }}>
            {is_facilitator ? 'You are facilitating this match' : 'You are matched with a facilitator'}
          </p>
        </div>

        <button
          onClick={handleEndMatch}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#ef4444',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontSize: '0.9rem',
            cursor: 'pointer'
          }}
        >
          <FontAwesomeIcon icon={faUserMinus} /> End Match
        </button>
      </div>

      {/* Buddy Info */}
      <div style={{
        backgroundColor: '#000',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '1.5rem',
        marginBottom: '1.5rem',
        display: 'flex',
        alignItems: 'center',
        gap: '1.5rem'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#FFB6C1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '2rem',
          fontWeight: '700',
          color: '#000'
        }}>
          {buddy?.full_name?.charAt(0)?.toUpperCase() || '?'}
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginBottom: '0.5rem',
            fontSize: '1.5rem'
          }}>
            {buddy?.full_name || 'Unknown'}
          </h3>
          <div style={{ color: '#999', fontSize: '0.95rem' }}>
            <p style={{ margin: '0.25rem 0' }}>
              Matched since {formatDate(match.matched_at)}
            </p>
            {match.last_interaction && (
              <p style={{ margin: '0.25rem 0' }}>
                Last interaction: {formatDate(match.last_interaction)}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <button
          onClick={() => navigate(`/buddy/chat/${match.id}`)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3b82f6',
            border: 'none',
            borderRadius: '6px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <FontAwesomeIcon icon={faMessage} /> Chat with {buddy?.full_name?.split(' ')[0] || 'Buddy'}
        </button>


      </div>

      {/* Log Interaction Form */}
      {showLogInteraction && isPremium && (
        <div style={{
          backgroundColor: '#000',
          border: '1px solid #333',
          borderRadius: '8px',
          padding: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <h3 style={{ color: '#FFB6C1', marginBottom: '1rem' }}>
            Log an Interaction
          </h3>

          {/* Interaction Type */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              color: '#ccc',
              fontSize: '0.95rem'
            }}>
              Type of Interaction
            </label>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              {INTERACTION_TYPES.map(type => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setInteractionType(type.value)}
                  style={{
                    flex: '1 1 150px',
                    padding: '0.75rem',
                    backgroundColor: interactionType === type.value ? type.color : '#1a1a1a',
                    border: `1px solid ${interactionType === type.value ? type.color : '#333'}`,
                    borderRadius: '8px',
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <FontAwesomeIcon icon={type.icon} /> {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '0.5rem', 
              color: '#ccc',
              fontSize: '0.95rem'
            }}>
              Notes
            </label>
            <textarea
              value={interactionNotes}
              onChange={(e) => setInteractionNotes(e.target.value)}
              placeholder="What did you discuss? How did it go?"
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Submit */}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={() => setShowLogInteraction(false)}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#ccc',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleLogInteraction}
              disabled={loading}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: '#FFB6C1',
                border: 'none',
                borderRadius: '8px',
                color: '#000',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Saving...' : 'Save Interaction'}
            </button>
          </div>
        </div>
      )}

      {/* Recent Interactions */}
      {recent_interactions && recent_interactions.length > 0 && (
        <div>
          <h3 style={{ 
            color: '#FFB6C1', 
            marginBottom: '1rem',
            fontSize: '1.25rem'
          }}>
            Recent Interactions
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {recent_interactions.map(interaction => (
              <div
                key={interaction.id}
                style={{
                  backgroundColor: '#000',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  padding: '1rem',
                  display: 'flex',
                  gap: '1rem'
                }}
              >
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  backgroundColor: getInteractionColor(interaction.interaction_type) + '30',
                  border: `2px solid ${getInteractionColor(interaction.interaction_type)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <FontAwesomeIcon 
                    icon={getInteractionIcon(interaction.interaction_type)} 
                    style={{ color: getInteractionColor(interaction.interaction_type) }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    marginBottom: '0.5rem',
                    flexWrap: 'wrap',
                    gap: '0.5rem'
                  }}>
                    <span style={{ 
                      color: '#FFB6C1',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {interaction.interaction_type.replace('_', ' ')}
                    </span>
                    <span style={{ color: '#666', fontSize: '0.85rem' }}>
                      {formatDate(interaction.created_at)}
                    </span>
                  </div>
                  <p style={{ color: '#ccc', margin: 0, fontSize: '0.95rem' }}>
                    {interaction.notes}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrentMatchView;
